/**
 * Deploy-target picker. Shared between the Project Settings dialog and
 * the New Project dialog so both surfaces show the same affordance for
 * setting up where "Deploy" sends content.
 *
 * The "None" option means no deploy target — Deploy button hidden,
 * Export button is the user's manual download path.
 */

import type { DeployTargetConfig } from '@bliksys/shared';
import { useId } from 'react';

import { LocalCredentialNotice } from './LocalCredentialNotice.js';

interface Props {
  target: DeployTargetConfig | undefined;
  onChange: (t: DeployTargetConfig | undefined) => void;
  /**
   * Free-mode call sites set this: the anonymous /deploy proxy refuses
   * webhook targets (SSRF hardening — they need a cloud org with an
   * allow-listed destination), so the option is shown but not selectable.
   * The cloud DeployTargetPicker leaves it off.
   */
  webhookCloudOnly?: boolean;
}

export function DeployTargetField({
  target,
  onChange,
  webhookCloudOnly = false,
}: Props): JSX.Element {
  const kindId = useId();
  const value = target?.kind ?? 'none';
  return (
    <>
      <p className="project-settings-section-hint">
        Where "Deploy" sends your content. Use Export buttons for plain downloads instead.
      </p>
      <div className="landing-field">
        <label htmlFor={kindId} className="sr-only">
          Target kind
        </label>
        <select
          id={kindId}
          className="input"
          value={value}
          onChange={(e) => {
            const kind = e.target.value;
            // Preserve fields when switching back to the same kind — users
            // often toggle to inspect another option.
            if (kind === 'none') onChange(undefined);
            else if (kind === 'webhook')
              onChange(target?.kind === 'webhook' ? target : { kind: 'webhook', url: '' });
            else if (kind === 'github')
              onChange(
                target?.kind === 'github'
                  ? target
                  : { kind: 'github', repo: '', branch: 'main', path: 'content.json' },
              );
            else if (kind === 'cloudflareKV')
              onChange(
                target?.kind === 'cloudflareKV'
                  ? target
                  : { kind: 'cloudflareKV', accountId: '', namespaceId: '', key: '', token: '' },
              );
          }}
        >
          <option value="none">None — hide the Deploy button</option>
          <option value="webhook" disabled={webhookCloudOnly}>
            {webhookCloudOnly ? 'POST to webhook — Bliksys Cloud only' : 'POST to webhook'}
          </option>
          <option value="github">Commit to GitHub repo</option>
          <option value="cloudflareKV">Write to Cloudflare KV</option>
        </select>
      </div>

      {target?.kind === 'webhook' && (
        <>
          {webhookCloudOnly && (
            <p className="project-settings-section-hint" role="note">
              Webhook deploys now require Bliksys Cloud: they deliver through your org&apos;s
              destination allow-list instead of an open proxy. Sign in to keep using this target, or
              switch to GitHub / Cloudflare KV.
            </p>
          )}
          <WebhookFields target={target} onChange={(t) => onChange(t)} />
        </>
      )}

      {target?.kind === 'github' && <GithubFields target={target} onChange={(t) => onChange(t)} />}

      {target?.kind === 'cloudflareKV' && (
        <CloudflareKVFields target={target} onChange={(t) => onChange(t)} />
      )}
    </>
  );
}

/**
 * Friendly label for a deploy-target kind. Used by the settings dialog
 * (collapsed-section badge) and by the recent-projects list. Lives
 * with the field component so adding a new kind updates one place.
 */
export function labelDeployKind(kind: DeployTargetConfig['kind']): string {
  switch (kind) {
    case 'webhook':
      return 'Webhook';
    case 'github':
      return 'GitHub';
    case 'cloudflareKV':
      return 'Cloudflare KV';
  }
}

function WebhookFields({
  target,
  onChange,
}: {
  target: Extract<DeployTargetConfig, { kind: 'webhook' }>;
  onChange: (t: Extract<DeployTargetConfig, { kind: 'webhook' }>) => void;
}): JSX.Element {
  const urlId = useId();
  const secretId = useId();
  return (
    <>
      <LocalCredentialNotice />
      <div className="landing-field">
        <label className="landing-label" htmlFor={urlId}>
          URL{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={urlId}
          type="url"
          className="input"
          placeholder="https://ci.example.com/deploy"
          value={target.url}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, url: e.target.value })}
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={secretId}>
          Secret (optional)
          <span className="landing-label-hint">
            If set, the API signs requests with HMAC-SHA256 in the <code>X-Bliksys-Signature</code>{' '}
            header.
          </span>
        </label>
        <input
          id={secretId}
          type="password"
          className="input"
          value={target.secret ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.length > 0) {
              onChange({ ...target, secret: v });
            } else {
              const { secret: _omit, ...rest } = target;
              onChange(rest);
            }
          }}
        />
      </div>
    </>
  );
}

/**
 * GitHub deploy fields. Captures owner/repo, branch, path, and an
 * optional Personal Access Token. The token field is `type="password"`
 * so it doesn't show up in screen recordings or browser autofill UIs.
 *
 * The PAT is stored alongside the rest of the project config in
 * localStorage. Trade-off accepted: convenience for users who run
 * Bliksys against their own laptop's browser and don't want to round-
 * trip through env vars or a server-side secret store. For multi-user
 * deployments, this should move to a server-side secret store.
 */
function GithubFields({
  target,
  onChange,
}: {
  target: Extract<DeployTargetConfig, { kind: 'github' }>;
  onChange: (t: Extract<DeployTargetConfig, { kind: 'github' }>) => void;
}): JSX.Element {
  const repoId = useId();
  const branchId = useId();
  const pathId = useId();
  const tokenId = useId();
  const messageId = useId();
  const modeId = useId();
  const prTitleId = useId();
  const prBodyId = useId();
  // Default to 'direct' when the target predates the PR-mode option.
  const mode = target.mode ?? 'direct';
  return (
    <>
      <LocalCredentialNotice />
      <div className="landing-field">
        <label className="landing-label" htmlFor={modeId}>
          Mode
          <span className="landing-label-hint">
            Direct commits land on the configured branch immediately. Pull request opens a PR
            against the branch instead — for editorial workflows that need a review before content
            goes live.
          </span>
        </label>
        <select
          id={modeId}
          className="input"
          value={mode}
          onChange={(e) =>
            onChange({ ...target, mode: e.target.value as 'direct' | 'pull-request' })
          }
        >
          <option value="direct">Direct commit</option>
          <option value="pull-request">Pull request</option>
        </select>
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={repoId}>
          Repository{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            In <code>owner/name</code> format.
          </span>
        </label>
        <input
          id={repoId}
          type="text"
          className="input"
          placeholder="acme/website-content"
          value={target.repo}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, repo: e.target.value })}
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={branchId}>
          {mode === 'pull-request' ? 'Base branch' : 'Branch'}{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          {mode === 'pull-request' && (
            <span className="landing-label-hint">
              Pull requests are opened against this branch. The commit goes to a fresh head branch
              named <code>{`bliksys/deploy-{timestamp}`}</code>.
            </span>
          )}
        </label>
        <input
          id={branchId}
          type="text"
          className="input"
          value={target.branch}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, branch: e.target.value })}
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={pathId}>
          Path{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            Where to write the file. e.g. <code>data/site.json</code>.
          </span>
        </label>
        <input
          id={pathId}
          type="text"
          className="input"
          value={target.path}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, path: e.target.value })}
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={tokenId}>
          Personal access token{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            Token with <code>contents: write</code> for the repo. Stored in your browser&apos;s
            local storage.
          </span>
        </label>
        <input
          id={tokenId}
          type="password"
          className="input"
          autoComplete="off"
          value={target.token ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.length > 0) {
              onChange({ ...target, token: v });
            } else {
              const { token: _omit, ...rest } = target;
              onChange(rest);
            }
          }}
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={messageId}>
          Commit message (optional)
          <span className="landing-label-hint">
            Default: &ldquo;Deploy content via Bliksys&rdquo;. Timestamp is appended automatically.
          </span>
        </label>
        <input
          id={messageId}
          type="text"
          className="input"
          placeholder="Deploy content via Bliksys"
          value={target.commitMessage ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.length > 0) {
              onChange({ ...target, commitMessage: v });
            } else {
              const { commitMessage: _omit, ...rest } = target;
              onChange(rest);
            }
          }}
        />
      </div>
      {mode === 'pull-request' && (
        <>
          <div className="landing-field">
            <label className="landing-label" htmlFor={prTitleId}>
              PR title (optional)
              <span className="landing-label-hint">Defaults to the commit message.</span>
            </label>
            <input
              id={prTitleId}
              type="text"
              className="input"
              value={target.prTitle ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length > 0) {
                  onChange({ ...target, prTitle: v });
                } else {
                  const { prTitle: _omit, ...rest } = target;
                  onChange(rest);
                }
              }}
            />
          </div>
          <div className="landing-field">
            <label className="landing-label" htmlFor={prBodyId}>
              PR description (optional)
            </label>
            <textarea
              id={prBodyId}
              className="input input-textarea"
              rows={3}
              value={target.prBody ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length > 0) {
                  onChange({ ...target, prBody: v });
                } else {
                  const { prBody: _omit, ...rest } = target;
                  onChange(rest);
                }
              }}
            />
          </div>
        </>
      )}
    </>
  );
}

/**
 * Cloudflare KV deploy fields. Captures the account ID, namespace ID,
 * key name, and an API token. The IDs are 32-char hex from the
 * Cloudflare dashboard URL / namespace settings; the API token must
 * have the `Workers KV Storage:Edit` permission.
 *
 * Note for users: if you actually want a Pages rebuild rather than
 * direct KV writes, use the Webhook target with your Pages Deploy
 * Hook URL — that's a different flow, not handled here.
 */
function CloudflareKVFields({
  target,
  onChange,
}: {
  target: Extract<DeployTargetConfig, { kind: 'cloudflareKV' }>;
  onChange: (t: Extract<DeployTargetConfig, { kind: 'cloudflareKV' }>) => void;
}): JSX.Element {
  const accountId = useId();
  const namespaceId = useId();
  const keyId = useId();
  const tokenId = useId();
  return (
    <>
      <LocalCredentialNotice />
      <div className="landing-field">
        <label className="landing-label" htmlFor={accountId}>
          Account ID{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">32-char hex from the Cloudflare dashboard URL.</span>
        </label>
        <input
          id={accountId}
          type="text"
          className="input"
          value={target.accountId}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, accountId: e.target.value })}
          spellCheck={false}
          autoComplete="off"
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={namespaceId}>
          KV namespace ID{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={namespaceId}
          type="text"
          className="input"
          value={target.namespaceId}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, namespaceId: e.target.value })}
          spellCheck={false}
          autoComplete="off"
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={keyId}>
          Key{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            The KV key the content is written to. e.g. <code>content/site.json</code>.
          </span>
        </label>
        <input
          id={keyId}
          type="text"
          className="input"
          value={target.key}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, key: e.target.value })}
        />
      </div>
      <div className="landing-field">
        <label className="landing-label" htmlFor={tokenId}>
          API token{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            Token with <code>Workers KV Storage:Edit</code>.
          </span>
        </label>
        <input
          id={tokenId}
          type="password"
          className="input"
          autoComplete="off"
          value={target.token}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...target, token: e.target.value })}
        />
      </div>
    </>
  );
}

/**
 * Validate a deploy target's user-supplied fields. Returns a map of
 * field-key → error message. Empty map means valid. Used by the
 * Project Settings dialog and the New Project dialog before saving
 * so the user doesn't end up with a half-configured deploy target
 * that fails silently at deploy time.
 *
 * Validation is intentionally schema-shape only — no network calls.
 * A connectivity ping (POST a no-op to the webhook URL, GET the
 * GitHub repo, GET the Cloudflare account) would be more thorough
 * but adds dependency on external services on the save path. The
 * deploy step itself surfaces those errors when they happen.
 */
export type DeployTargetErrors = Record<string, string>;

export function validateDeployTarget(target: DeployTargetConfig | undefined): DeployTargetErrors {
  if (target === undefined) return {};
  if (target.kind === 'webhook') {
    if (!target.url) return { url: 'URL is required.' };
    try {
      const parsed = new URL(target.url);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        return { url: 'URL must use http:// or https://.' };
      }
    } catch {
      return { url: 'URL is not valid.' };
    }
    return {};
  }
  if (target.kind === 'github') {
    const errors: DeployTargetErrors = {};
    if (!target.repo) errors.repo = 'Repository is required.';
    else if (!/^[^/]+\/[^/]+$/.test(target.repo))
      errors.repo = 'Repository must be in owner/name format.';
    if (!target.branch) errors.branch = 'Branch is required.';
    if (!target.path) errors.path = 'Path is required.';
    else if (target.path.startsWith('/')) errors.path = "Path must not start with '/'.";
    // Token is optional in the type but practically required for any
    // write — surface a clear warning rather than letting GitHub return
    // a cryptic 401 at deploy time.
    if (!target.token) errors.token = 'A token is required for write access.';
    return errors;
  }
  if (target.kind === 'cloudflareKV') {
    const errors: DeployTargetErrors = {};
    if (!target.accountId) errors.accountId = 'Account ID is required.';
    else if (!/^[0-9a-f]{32}$/i.test(target.accountId))
      errors.accountId = 'Account ID must be 32 hex characters.';
    if (!target.namespaceId) errors.namespaceId = 'Namespace ID is required.';
    else if (!/^[0-9a-f]{32}$/i.test(target.namespaceId))
      errors.namespaceId = 'Namespace ID must be 32 hex characters.';
    if (!target.key) errors.key = 'Key is required.';
    if (!target.token) errors.token = 'API token is required.';
    return errors;
  }
  return {};
}
